import * as THREE from "three";
import { floorplannerStore } from "../store/floorplannerStore";
import {
  isDoubleDoorType,
  isDoorType,
  isSlidingDoorType,
  isRulerLineType,
  isSingleLineType,
  isSymbolType,
  isWallType,
  isWindowType,
  isVoidType,
  isRectStairsType,
  isTurnStairsType,
  isBookShelfType,
  isSmartTableType,
  isAreaFillSymbolType,
  isAreaSymbolType,
  SymbolType,
  WallType,
  SingleLineType,
  RulerLineType,
  FloorplannerSelectionType,
} from "../types/floorplannerTypes";
import { resolveSmartTableLayout } from "../features/floorplan3d/symbols/dining/smartTableLayout";
import { resolveBookShelfLayout } from "../features/floorplan3d/symbols/livingRoom/bookShelfLayout";
import { getTurnStairsPlanBounds } from "../features/floorplan3d/symbols/primitives/stairs/stairGeometry";

export type SelectionItem = WallType | SymbolType | SingleLineType | RulerLineType;

export const isSelectionBoundsItem = (
  selection: FloorplannerSelectionType,
): selection is SelectionItem =>
  isSymbolType(selection) ||
  isWallType(selection) ||
  isSingleLineType(selection) ||
  isRulerLineType(selection);

export type SelectionBounds = {
  min: THREE.Vector2;
  max: THREE.Vector2;
  width: number;
  height: number;
  center: THREE.Vector2;
  corners: [THREE.Vector2, THREE.Vector2, THREE.Vector2, THREE.Vector2];
  outline: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3];
};

type SelectionBoundsOptions = {
  padding?: number;
};

export const getSelectionBounds = (
  selections: SelectionItem[],
  options: SelectionBoundsOptions = {},
): SelectionBounds | null => {
  if (!selections.length) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  selections.forEach((object) => {
    if (isSymbolType(object)) {
      const symbol = floorplannerStore.symbolsMap.get(object.id);
      if (!symbol) return;

      let points: THREE.Vector3[] = [];

      if (isDoubleDoorType(symbol)) {
        const width = symbol.doubleDoorWidth * 2;
        const height = symbol.doubleDoorWidth;
        points = [
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(width, 0, 0),
          new THREE.Vector3(width, height, 0),
          new THREE.Vector3(0, height, 0),
        ];
      } else if (isSlidingDoorType(symbol)) {
        // Leaf + clear passage beside it (matches wall opening / 3D assembly).
        const width = symbol.doorWidth * 2;
        const height = symbol.doorWidth;
        points = [
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(width, 0, 0),
          new THREE.Vector3(width, height, 0),
          new THREE.Vector3(0, height, 0),
        ];
      } else if (isDoorType(symbol)) {
        const width = symbol.doorWidth;
        const height = width;
        points = [
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(width, 0, 0),
          new THREE.Vector3(width, height, 0),
          new THREE.Vector3(0, height, 0),
        ];
      } else if (isWindowType(symbol)) {
        const width = symbol.windowLength;
        const height = symbol.windowWidth;
        points = [
          new THREE.Vector3(-width / 2, -height / 2, 0),
          new THREE.Vector3(width / 2, -height / 2, 0),
          new THREE.Vector3(width / 2, height / 2, 0),
          new THREE.Vector3(-width / 2, height / 2, 0),
        ];
      } else if (isVoidType(symbol)) {
        const width = symbol.voidLength;
        const height = symbol.voidWidth;
        points = [
          new THREE.Vector3(-width / 2, -height / 2, 0),
          new THREE.Vector3(width / 2, -height / 2, 0),
          new THREE.Vector3(width / 2, height / 2, 0),
          new THREE.Vector3(-width / 2, height / 2, 0),
        ];
      } else if (isRectStairsType(symbol)) {
        const width = symbol.rectStairsWidth;
        const height = symbol.rectStairsHeight;
        const flipX = symbol.flipHorizontal ? -1 : 1;
        const flipY = symbol.flipVertical ? -1 : 1;
        points = [
          new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, height / 2 * flipY, 0),
          new THREE.Vector3(-width / 2 * flipX, height / 2 * flipY, 0),
        ];
      } else if (isTurnStairsType(symbol)) {
        const bounds = getTurnStairsPlanBounds(symbol, {
          defaultStepSize: symbol.stairStepSize ?? 0.32,
        });
        const width = bounds.width;
        const height = bounds.depth;
        const flipX = symbol.flipHorizontal ? -1 : 1;
        const flipY = symbol.flipVertical ? -1 : 1;
        points = [
          new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, height / 2 * flipY, 0),
          new THREE.Vector3(-width / 2 * flipX, height / 2 * flipY, 0),
        ];
      } else if ("squareWidth" in symbol && "squareHeight" in symbol) {
        const width = Number(symbol.squareWidth);
        const height = Number(symbol.squareHeight);
        const flipX = symbol.flipHorizontal ? -1 : 1;
        const flipY = symbol.flipVertical ? -1 : 1;
        points = [
          new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, height / 2 * flipY, 0),
          new THREE.Vector3(-width / 2 * flipX, height / 2 * flipY, 0),
        ];
      } else if ("triangleWidth" in symbol && "triangleHeight" in symbol) {
        const width = Number(symbol.triangleWidth);
        const height = Number(symbol.triangleHeight);
        const flipX = symbol.flipHorizontal ? -1 : 1;
        const flipY = symbol.flipVertical ? -1 : 1;
        points = [
          new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, height / 2 * flipY, 0),
          new THREE.Vector3(-width / 2 * flipX, height / 2 * flipY, 0),
        ];
      } else if ("circleWidth" in symbol && "circleHeight" in symbol) {
        const width = Number(symbol.circleWidth);
        const height = Number(symbol.circleHeight);
        const flipX = symbol.flipHorizontal ? -1 : 1;
        const flipY = symbol.flipVertical ? -1 : 1;
        points = [
          new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, height / 2 * flipY, 0),
          new THREE.Vector3(-width / 2 * flipX, height / 2 * flipY, 0),
        ];
      } else if ("polygonWidth" in symbol && "polygonHeight" in symbol) {
        const width = Number(symbol.polygonWidth);
        const height = Number(symbol.polygonHeight);
        const flipX = symbol.flipHorizontal ? -1 : 1;
        const flipY = symbol.flipVertical ? -1 : 1;
        points = [
          new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, -height / 2 * flipY, 0),
          new THREE.Vector3(width / 2 * flipX, height / 2 * flipY, 0),
          new THREE.Vector3(-width / 2 * flipX, height / 2 * flipY, 0),
        ];
      } else if (isSmartTableType(symbol)) {
        const bounds = resolveSmartTableLayout(symbol.smartTable).bounds;
        points = [
          new THREE.Vector3(bounds.minX, bounds.minY, 0),
          new THREE.Vector3(bounds.maxX, bounds.minY, 0),
          new THREE.Vector3(bounds.maxX, bounds.maxY, 0),
          new THREE.Vector3(bounds.minX, bounds.maxY, 0),
        ];
      } else if (isBookShelfType(symbol)) {
        const bounds = resolveBookShelfLayout(symbol.bookShelf).bounds;
        points = [
          new THREE.Vector3(bounds.minX, bounds.minY, 0),
          new THREE.Vector3(bounds.maxX, bounds.minY, 0),
          new THREE.Vector3(bounds.maxX, bounds.maxY, 0),
          new THREE.Vector3(bounds.minX, bounds.maxY, 0),
        ];
      } else if ((isAreaSymbolType(symbol) || isAreaFillSymbolType(symbol)) && Array.isArray(symbol.vertices)) {
        points = symbol.vertices.map((vertex) => new THREE.Vector3(vertex.x, vertex.y, 0));
      } else {
        const width = (symbol as any)?.width ?? 1;
        const height = (symbol as any)?.height ?? 1;
        points = [
          new THREE.Vector3(-width / 2, -height / 2, 0),
          new THREE.Vector3(width / 2, -height / 2, 0),
          new THREE.Vector3(width / 2, height / 2, 0),
          new THREE.Vector3(-width / 2, height / 2, 0),
        ];
      }

      if (symbol.rotation !== undefined) {
        if (
          isWindowType(symbol) ||
          isRectStairsType(symbol) ||
          (!isDoorType(symbol) && !isSlidingDoorType(symbol) && !isDoubleDoorType(symbol))
        ) {
          points.forEach((point) => {
            point.applyAxisAngle(new THREE.Vector3(0, 0, 1), symbol.rotation || 0);
          });
        } else {
          points.forEach((point) => {
            point.applyAxisAngle(new THREE.Vector3(0, 0, 1), symbol.rotation || 0);
          });
        }
      }

      points.forEach((point) => {
        point.add(new THREE.Vector3(symbol.position.x, symbol.position.y, 0));
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      });
    } else if (isWallType(object) || isSingleLineType(object) || isRulerLineType(object)) {
      const obj = floorplannerStore.findObjectId(object.id);
      if (!obj) return;

      minX = Math.min(minX, obj.start.x, obj.end.x);
      maxX = Math.max(maxX, obj.start.x, obj.end.x);
      minY = Math.min(minY, obj.start.y, obj.end.y);
      maxY = Math.max(maxY, obj.start.y, obj.end.y);

      if (isWallType(object)) {
        const wall = obj as WallType;
        const wallWidth = wall.wallWidth || floorplannerStore.wallWidth;
        const dx = obj.end.x - obj.start.x;
        const dy = obj.end.y - obj.start.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        if (length > 0) {
          const nx = (-dy / length) * wallWidth / 2;
          const ny = (dx / length) * wallWidth / 2;
          const wallPoints = [
            new THREE.Vector3(obj.start.x + nx, obj.start.y + ny, 0),
            new THREE.Vector3(obj.start.x - nx, obj.start.y - ny, 0),
            new THREE.Vector3(obj.end.x + nx, obj.end.y + ny, 0),
            new THREE.Vector3(obj.end.x - nx, obj.end.y - ny, 0),
          ];
          wallPoints.forEach((point) => {
            minX = Math.min(minX, point.x);
            maxX = Math.max(maxX, point.x);
            minY = Math.min(minY, point.y);
            maxY = Math.max(maxY, point.y);
          });
        }
      }
    }
  });

  if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
    return null;
  }

  const padding = options.padding ?? 0;
  minX -= padding;
  minY -= padding;
  maxX += padding;
  maxY += padding;

  const width = maxX - minX;
  const height = maxY - minY;

  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return null;
  }

  const min = new THREE.Vector2(minX, minY);
  const max = new THREE.Vector2(maxX, maxY);
  const center = new THREE.Vector2(minX + width / 2, minY + height / 2);

  const corners: [THREE.Vector2, THREE.Vector2, THREE.Vector2, THREE.Vector2] = [
    new THREE.Vector2(minX, minY),
    new THREE.Vector2(maxX, minY),
    new THREE.Vector2(maxX, maxY),
    new THREE.Vector2(minX, maxY),
  ];

  const outline: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3] = [
    new THREE.Vector3(minX, minY, 0),
    new THREE.Vector3(maxX, minY, 0),
    new THREE.Vector3(maxX, maxY, 0),
    new THREE.Vector3(minX, maxY, 0),
    new THREE.Vector3(minX, minY, 0),
  ];

  return {
    min,
    max,
    width,
    height,
    center,
    corners,
    outline,
  };
};
